import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Users, Plus, ArrowRight, CheckCircle, Clock } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FamilyDashboardCard — compact summary shown on the user's main dashboard
 * only when their selected mode is 'family'. Pulls the same aggregate KPIs
 * the /app/family page shows and offers a one-click jump to manage members.
 */
const FamilyDashboardCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/me/family/dashboard`);
        setData(res.data);
      } catch (err) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Card className="card-default">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  // No family yet — invite the user to create one
  if (!data || !data.has_family) {
    return (
      <Card className="card-default" data-testid="family-dashboard-card-empty">
        <CardContent className="p-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-white font-medium">Aún no has creado tu familia</p>
              <p className="text-zinc-500 text-sm">
                Agrega hasta 4 perfiles con planes individuales en tu Plan Salud Familiar.
              </p>
            </div>
          </div>
          <Link to="/app/family">
            <Button className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Crear familia
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-default" data-testid="family-dashboard-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-emerald-400 text-xs uppercase tracking-wider font-medium">Tu familia</p>
              <h3 className="font-teko text-2xl text-white">{data.family_name}</h3>
            </div>
          </div>
          <Link to="/app/family">
            <Button variant="ghost" className="text-zinc-400 hover:text-white">
              Gestionar <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-3 gap-3 pt-2 border-t border-zinc-800">
          <div>
            <p className="text-zinc-500 text-xs uppercase tracking-wider">Perfiles</p>
            <p className="font-teko text-2xl text-white">{data.members_total}</p>
          </div>
          <div>
            <p className="text-zinc-500 text-xs uppercase tracking-wider flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-primary" /> Con plan
            </p>
            <p className="font-teko text-2xl text-white">{data.members_with_plan}</p>
          </div>
          <div>
            <p className="text-zinc-500 text-xs uppercase tracking-wider flex items-center gap-1">
              <Clock className="w-3 h-3 text-purple-400" /> Checkpoints
            </p>
            <p className="font-teko text-2xl text-white">{data.pending_checkpoints}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FamilyDashboardCard;
